import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TableFooter,
  TablePagination,
  Chip, 
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { format } from 'date-fns';
import { 
  getInventoryAdjustments, 
  getInventoryAdjustmentById, 
  InventoryAdjustment, 
  InventoryAdjustmentItem 
} from '../firebase/inventoryAdjustmentServices';
import { formatCurrency } from '../utils/formatters';
import MonthlyInventorySummary from '../components/inventory/MonthlyInventorySummary';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Define tab values
type TabValue = 'all' | 'sales' | 'manual' | 'purchase' | 'summary';

const InventoryAdjustmentHistory: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [adjustments, setAdjustments] = useState<InventoryAdjustment[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedAdjustment, setSelectedAdjustment] = useState<InventoryAdjustment | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [filterType, setFilterType] = useState<'all' | 'positive' | 'negative'>('all');
  
  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // Tab state for transaction types
  const [tabValue, setTabValue] = useState<TabValue>('all');
  
  // Time range filtering
  const [timeRange, setTimeRange] = useState<'monthly' | 'quarterly' | 'yearly' | 'custom'>('monthly');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  
  // Generate array of years (current year and 5 years back)
  const availableYears = Array.from({length: 6}, (_, i) => new Date().getFullYear() - i);
  
  // Month names for dropdown
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Set date range based on selected time range, year and month
  useEffect(() => {
    if (timeRange === 'monthly' && selectedYear && selectedMonth !== undefined) {
      // For monthly view
      const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
      const lastDayOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
      
      setStartDate(firstDayOfMonth);
      setEndDate(lastDayOfMonth);
    } else if (timeRange === 'custom') {
      // Keep custom date range as is
    } else {
      // Reset dates for other views (to be implemented)
      setStartDate(null);
      setEndDate(null);
    }
  }, [timeRange, selectedYear, selectedMonth]);

  // Fetch inventory adjustments
  useEffect(() => {
    const fetchAdjustments = async () => {
      setLoading(true);
      try {
        // Always fetch all adjustments and filter client-side for more flexibility
        // Increased limit to 1000 to ensure we get all transactions
        const data = await getInventoryAdjustments(1000, undefined, filterType !== 'all' ? filterType : undefined);
        setAdjustments(data);
        // Reset to first page when data changes
        setPage(0);
      } catch (error) {
        console.error('Error fetching inventory adjustments:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAdjustments();
  }, [filterType]);

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Handle filter type change
  const handleFilterTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilterType: 'all' | 'positive' | 'negative' | null
  ) => {
    if (newFilterType !== null) {
      setFilterType(newFilterType);
    }
  };
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setTabValue(newValue);
    // Reset to first page when changing tabs
    setPage(0);
  };
  
  // Handle time range change
  const handleTimeRangeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setTimeRange(event.target.value as 'monthly' | 'quarterly' | 'yearly' | 'custom');
  };
  
  // Handle year change
  const handleYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedYear(event.target.value as number);
  };
  
  // Handle month change
  const handleMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedMonth(event.target.value as number);
  };

  // Filter adjustments based on search query, date range, and transaction type
  const filteredAdjustments = adjustments.filter(adjustment => {
    // Apply search filter
    let matchesSearch = 
      adjustment.adjustmentNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      adjustment.reason.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Also search in product names and SKUs
    if (!matchesSearch && adjustment.items && adjustment.items.length > 0) {
      // Check if any product name or SKU matches the search query
      matchesSearch = adjustment.items.some(item => 
        (item.productName && item.productName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.productSku && item.productSku.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Apply date filters if set
    let matchesDateRange = true;
    const adjustmentDate = adjustment.adjustmentDate instanceof Date 
      ? adjustment.adjustmentDate 
      : new Date((adjustment.adjustmentDate as any).seconds * 1000);
    
    if (startDate) {
      matchesDateRange = matchesDateRange && adjustmentDate >= startDate;
    }
    
    if (endDate) {
      // Add one day to include the end date fully
      const endDatePlusOne = new Date(endDate);
      endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
      matchesDateRange = matchesDateRange && adjustmentDate < endDatePlusOne;
    }
    
    // Apply tab filter
    let matchesTabFilter = true;
    if (tabValue !== 'all') {
      // Get reason and check for subtype in different places
      const reason = adjustment.reason?.toLowerCase() || '';
      
      // Extract subtype from items array or direct property
      let subtype = '';
      
      // Check items array first
      if (adjustment.items && adjustment.items.length > 0) {
        // Look through all items for a subtype
        for (const item of adjustment.items) {
          if (item.subtype) {
            subtype = item.subtype;
            break;
          }
        }
      }
      
      // If not found in items, check other possible locations
      if (!subtype) {
        subtype = adjustment.subtype || 
                 adjustment.transactionData?.subtype || 
                 '';
      }
      
      if (tabValue === 'sales') {
        // Match sale transactions and void sale restorations
        matchesTabFilter = 
          // Check subtype
          subtype === 'sale_deduction' || 
          subtype === 'void_sale_restoration' || 
          // Check type
          adjustment.type === 'sale' ||
          // Check reason text patterns
          reason.includes('sale:') || 
          reason.includes('void sale:') ||
          reason.includes('sale-') ||
          reason.includes('sale ');
      } else if (tabValue === 'purchase') {
        // Match purchase receipt transactions
        matchesTabFilter = 
          // Check subtype
          subtype === 'purchase_receipt' || 
          // Check reason text patterns
          reason.includes('purchase receipt') ||
          reason.includes('pr-');
      } else if (tabValue === 'manual') {
        // Match manual adjustments (everything else)
        matchesTabFilter = 
          // Check subtypes
          (subtype === 'manual_addition' || 
           subtype === 'damage' || 
           subtype === 'expiry' || 
           subtype === 'missing' || 
           subtype === 'sample' || 
           subtype === 'salon_use' || 
           subtype === 'manual_reduction') || 
          // Check reason text patterns
          (reason.includes('free product') || 
           reason.includes('damaged') || 
           reason.includes('expired') || 
           reason.includes('missing') || 
           reason.includes('sample') || 
           reason.includes('salon use'));
      }
    }
    
    return matchesSearch && matchesDateRange && matchesTabFilter;
  });

  // View adjustment details
  const handleViewDetails = async (adjustmentId: string) => {
    try {
      const adjustment = await getInventoryAdjustmentById(adjustmentId);
      if (adjustment) {
        setSelectedAdjustment(adjustment);
        setDetailsOpen(true);
      }
    } catch (error) {
      console.error('Error fetching adjustment details:', error);
    }
  };

  // Close details dialog
  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setTimeRange('monthly');
    setSelectedYear(new Date().getFullYear());
    setSelectedMonth(new Date().getMonth());
    setStartDate(null);
    setEndDate(null);
    setFilterType('all');
    setPage(0);
  };
  
  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Inventory Adjustment History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View inventory adjustments and their financial impact
        </Typography>
      </Box>
      
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search by adjustment #, reason, product name or SKU..."
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: '250px' }}
            />
            
            {/* Time Range Selector */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={handleTimeRangeChange as any}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
            
            {/* Year Selector */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={handleYearChange as any}
              >
                {availableYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Month Selector - only show for monthly view */}
            {timeRange === 'monthly' && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={handleMonthChange as any}
                >
                  {monthNames.map((month, index) => (
                    <MenuItem key={index} value={index}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {/* Only show date pickers for custom range */}
            {timeRange === 'custom' && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            )}
            
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Box>
          
          <Box>
            <Typography variant="body2" gutterBottom>
              Filter by adjustment type:
            </Typography>
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={handleFilterTypeChange}
              size="small"
            >
              <ToggleButton value="all">
                All
              </ToggleButton>
              <ToggleButton value="positive">
                <TrendingUpIcon sx={{ mr: 1 }} />
                Positive
              </ToggleButton>
              <ToggleButton value="negative">
                <TrendingDownIcon sx={{ mr: 1 }} />
                Negative
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {/* Transaction Type Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              aria-label="transaction type tabs"
              sx={{ mb: 1 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                icon={<InventoryIcon fontSize="small" />} 
                iconPosition="start" 
                label="All Transactions" 
                value="all" 
              />
              <Tab 
                icon={<ReceiptLongIcon fontSize="small" />} 
                iconPosition="start" 
                label="Sales" 
                value="sales" 
              />
              <Tab 
                icon={<InventoryIcon fontSize="small" />} 
                iconPosition="start" 
                label="Manual Adjustments" 
                value="manual" 
              />
              <Tab 
                icon={<ReceiptLongIcon fontSize="small" />} 
                iconPosition="start" 
                label="Purchase Receipts" 
                value="purchase" 
              />
              <Tab 
                icon={<AssessmentIcon fontSize="small" />} 
                iconPosition="start" 
                label="Monthly Summary" 
                value="summary" 
              />
            </Tabs>
          </Box>
        </Stack>
      </Paper>
      
      {/* Content based on selected tab */}
      {tabValue === 'summary' ? (
        // Monthly Summary View
        <MonthlyInventorySummary year={selectedYear} month={selectedMonth} />
      ) : (
        // Regular Adjustments Table for all other tabs
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Adjustment #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredAdjustments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No adjustments found
                  </TableCell>
                </TableRow>
              ) : (
                // Apply pagination to the filtered adjustments
                filteredAdjustments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((adjustment) => {
                    const isPositive = adjustment.totalValueChange >= 0;
                    return (
                      <TableRow key={adjustment.id}>
                        <TableCell>{adjustment.adjustmentNumber}</TableCell>
                        <TableCell>
                          {adjustment.adjustmentDate instanceof Date
                            ? format(adjustment.adjustmentDate, 'MM/dd/yyyy')
                            : format(new Date((adjustment.adjustmentDate as any).seconds * 1000), 'MM/dd/yyyy')
                          }
                        </TableCell>
                        <TableCell>{adjustment.reason}</TableCell>
                        <TableCell>
                          {adjustment.items && adjustment.items.length > 0 ? (
                            adjustment.items.length === 1 ? (
                              adjustment.items[0].productName
                            ) : (
                              <>
                                {adjustment.items[0].productName}
                                <Typography variant="caption" color="text.secondary" display="block">
                                  +{adjustment.items.length - 1} more
                                </Typography>
                              </>
                            )
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          {adjustment.items && adjustment.items.length > 0 ? (
                            adjustment.items.length === 1 ? (
                              adjustment.items[0].productSku || 'N/A'
                            ) : (
                              <>
                                {adjustment.items[0].productSku || 'N/A'}
                                <Typography variant="caption" color="text.secondary" display="block">
                                  +{adjustment.items.length - 1} more
                                </Typography>
                              </>
                            )
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {adjustment.items && adjustment.items.length > 0 ? (
                            adjustment.items.length === 1 ? (
                              <Typography
                                variant="body2"
                                color={
                                  adjustment.items[0].quantityChange > 0
                                    ? 'success.main'
                                    : adjustment.items[0].quantityChange < 0
                                    ? 'error.main'
                                    : 'text.primary'
                                }
                              >
                                {adjustment.items[0].quantityChange > 0 ? '+' : ''}{adjustment.items[0].quantityChange}
                              </Typography>
                            ) : (
                              <>
                                <Typography
                                  variant="body2"
                                  color={
                                    adjustment.items[0].quantityChange > 0
                                      ? 'success.main'
                                      : adjustment.items[0].quantityChange < 0
                                      ? 'error.main'
                                      : 'text.primary'
                                  }
                                >
                                  {adjustment.items[0].quantityChange > 0 ? '+' : ''}{adjustment.items[0].quantityChange}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  +{adjustment.items.length - 1} more
                                </Typography>
                              </>
                            )
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => adjustment.id && handleViewDetails(adjustment.id)}
                            title="View Details"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  count={filteredAdjustments.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  labelRowsPerPage="Rows per page:"
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
      
      {/* Adjustment Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedAdjustment && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InventoryIcon sx={{ mr: 1 }} />
                Inventory Adjustment #{selectedAdjustment.adjustmentNumber}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2">Date</Typography>
                  <Typography variant="body1">
                    {format(
                      selectedAdjustment.adjustmentDate instanceof Date
                        ? selectedAdjustment.adjustmentDate
                        : new Date((selectedAdjustment.adjustmentDate as any).seconds * 1000),
                      'dd/MM/yyyy'
                    )}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2">Reason</Typography>
                  <Typography variant="body1">{selectedAdjustment.reason}</Typography>
                </Box>
                {/* Total Value Change section removed as requested */}
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2">Created By</Typography>
                  <Typography variant="body1">
                    {selectedAdjustment.createdBy || 'System'}
                  </Typography>
                </Box>
              </Box>
              
              {selectedAdjustment.notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Notes</Typography>
                  <Typography variant="body2">{selectedAdjustment.notes}</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Adjusted Items
              </Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Before</TableCell>
                      <TableCell align="right">After</TableCell>
                      <TableCell align="right">Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedAdjustment.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.productSku}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell align="right">{item.quantityBefore}</TableCell>
                        <TableCell align="right">{item.quantityAfter}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={
                              item.quantityChange > 0
                                ? 'success.main'
                                : item.quantityChange < 0
                                ? 'error.main'
                                : 'text.primary'
                            }
                          >
                            {item.quantityChange > 0 ? '+' : ''}{item.quantityChange}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* GL Transaction section removed */}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default InventoryAdjustmentHistory;
